import { formatPKR } from '../api';
import PaymentStatusBadge from './PaymentStatusBadge';
import { formatPaymentMethodDisplay } from '../utils/paymentUtils';

const Detail = ({ label, value }) => (
  <div>
    <dt className="text-[11px] uppercase tracking-wider text-lumora-text/45 mb-1">{label}</dt>
    <dd className="text-sm text-lumora-dark-wood break-words">{value ?? '—'}</dd>
  </div>
);

function resolvePaymentDisplay(order) {
  return formatPaymentMethodDisplay(order);
}

function resolveTransactionStatus(order) {
  const ps = order.paymentStatus || '';
  if (ps === 'Verified') return 'Verified';
  if (ps === 'Rejected') return 'Rejected';
  if (ps === 'Pending Verification') return 'Awaiting Verification';
  if (ps === 'Pending (Cash on Delivery)') return 'Pending (COD)';
  return ps || '—';
}

function resolveVerificationStatus(order) {
  const ps = order.paymentStatus || '';
  const method = String(order.paymentMethod || '');
  if (method === 'COD' || /cash on delivery/i.test(method)) return 'N/A (COD)';
  if (ps === 'Verified')             return '✓ Verified';
  if (ps === 'Rejected')             return '✗ Rejected';
  return 'Pending';
}

export default function OrderDetailsPanel({ order }) {
  const isOnline = order.paymentMethod === 'Online Payment';

  return (
    <div className="space-y-5">
      {/* ── Delivery & Contact ── */}
      <div className="bg-lumora-secondary/25 rounded-xl p-5 md:p-6 border border-lumora-secondary/60">
        <p className="text-xs uppercase tracking-wider text-lumora-text/50 mb-4">
          Delivery &amp; contact details
        </p>
        <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
          <Detail label="Full name"    value={order.customerName}  />
          <Detail label="Email"        value={order.customerEmail} />
          <Detail label="Phone"        value={order.customerPhone} />
          <Detail label="Address"      value={order.address}       />
          <Detail label="City"         value={order.city}          />
          <Detail label="Province"     value={order.province}      />
          <Detail label="Postal code"  value={order.postalCode}    />
        </dl>
      </div>

      {/* ── Payment Information ── */}
      <div className="bg-lumora-secondary/25 rounded-xl p-5 md:p-6 border border-lumora-secondary/60">
        <p className="text-xs uppercase tracking-wider text-lumora-text/50 mb-4">
          Payment information
        </p>
        <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
          {/* Payment Method */}
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-lumora-text/45 mb-1">
              Payment Method
            </dt>
            <dd className="text-sm text-lumora-dark-wood font-medium">
              {resolvePaymentDisplay(order)}
            </dd>
          </div>

          {/* Payment Status — badge */}
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-lumora-text/45 mb-1">
              Payment Status
            </dt>
            <dd>
              <PaymentStatusBadge status={order.paymentStatus} />
            </dd>
          </div>

          {/* Transaction Status */}
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-lumora-text/45 mb-1">
              Transaction Status
            </dt>
            <dd className="text-sm text-lumora-dark-wood">
              {resolveTransactionStatus(order)}
            </dd>
          </div>

          {/* Order Total */}
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-lumora-text/45 mb-1">
              Order Total
            </dt>
            <dd className="text-sm text-lumora-dark-wood font-semibold">
              {formatPKR(order.orderTotal)}
            </dd>
          </div>

          {/* Subtotal */}
          <Detail label="Subtotal"  value={formatPKR(order.subtotal)} />
          <Detail label="Delivery"  value={formatPKR(order.delivery)} />

          {/* Verification Status */}
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-lumora-text/45 mb-1">
              Verification Status
            </dt>
            <dd className={`text-sm font-medium ${
              order.paymentStatus === 'Verified'
                ? 'text-green-700'
                : order.paymentStatus === 'Rejected'
                  ? 'text-red-600'
                  : 'text-lumora-dark-wood'
            }`}>
              {resolveVerificationStatus(order)}
            </dd>
          </div>

          {/* Verification Date — only shown once verified */}
          {isOnline && order.paymentStatus === 'Verified' && (
            <Detail
              label="Verified on"
              value={order.verifiedAt
                ? new Date(order.verifiedAt).toLocaleString('en-PK')
                : 'Verified'}
            />
          )}
        </dl>
      </div>
    </div>
  );
}
